/**
 * @module UseVoipCallParticipants
 * @description Phase 16 orchestrator for the extracted VoIP caller/callee participant hooks.
 * RHF note: coordinates explicit field registration with filtered selection state under shouldUnregister:true.
 */

import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { get, isEmpty } from 'lodash';
import { useFilterStyles } from '../../../../../panels/voip-test-now-panels/VoipPulseTestConfig';
import useNgpFormContext from '../../../../../../utilities/NgpFormContextProviderUtilities';
import { useRegisterFields } from '../../hooks/UseRegisterFields';
import { useVoipPulseAgents } from './UseVoipPulseAgents';
import { useVoipAgentFiltering } from './UseVoipAgentFiltering';
import { useVoipPartySelection } from './UseVoipPartySelection';
import {
    AGENT_ALL_FIELD_NAME,
    AGENT_IDS_FIELD_NAME,
    TESTCONFIG_CALLEE_FIELD_NAME,
    TESTCONFIG_CALLER_FIELD_NAME,
    TESTCONFIG_CALLVIA_FIELD_NAME,
} from '../../../ServiceTestsConstants';

const VOIP_PARTICIPANT_FIELDS = [
    TESTCONFIG_CALLER_FIELD_NAME,
    TESTCONFIG_CALLEE_FIELD_NAME,
    TESTCONFIG_CALLVIA_FIELD_NAME,
    AGENT_IDS_FIELD_NAME,
    AGENT_ALL_FIELD_NAME,
];

export function useVoipCallParticipants() {
    const filterClasses = useFilterStyles();
    const {
        agents,
        agentsById,
        isLoading,
        selectedCallerId,
        setSelectedCallerId,
        selectedCalleeId,
        setSelectedCalleeId,
    } = useVoipPulseAgents();
    const { formData } = useNgpFormContext();
    const { setValue, getValues } = useFormContext();
    const [callerFilterText, setCallerFilterText] = useState('');
    const [calleeFilterText, setCalleeFilterText] = useState('');

    // Register VoIP Pulse-specific fields so setValue() persists them.
    // The global form uses shouldUnregister:true, which silently discards
    // setValue() for unregistered fields. VoIP Pulse hides the standard
    // TestRunOnnPointsSection (which normally registers agents.*), so we
    // must register everything here.
    // Note: testConfig.jitter is registered in CallSettingsFieldSection
    // (shared by both VoipPulse and VoipPhone).
    useRegisterFields(VOIP_PARTICIPANT_FIELDS);

    // Initialize selections from saved test data once agents are loaded
    useEffect(() => {
        if (isLoading || isEmpty(agents)) return;

        const callerId = get(formData, 'testConfig.caller.ids.0.id', '');
        const calleeId = get(formData, 'testConfig.callee.ids.0.id', '');

        // Only set if the agent still exists in the current agent list
        if (callerId && agentsById[callerId]) {
            setSelectedCallerId(callerId);
        }
        if (calleeId && agentsById[calleeId]) {
            setSelectedCalleeId(calleeId);
        }
    }, [isLoading, agents, agentsById, formData, setSelectedCallerId, setSelectedCalleeId]);

    const callerAgent = selectedCallerId ? agentsById[selectedCallerId] : null;
    const calleeAgent = selectedCalleeId ? agentsById[selectedCalleeId] : null;

    // Callee Instructions visible when: both parties selected, not same agent,
    // and caller is NOT in direct-calling mode (direct calling auto-handles callVia).
    const showCalleeInstructions = !!(
        callerAgent &&
        calleeAgent &&
        selectedCallerId !== selectedCalleeId &&
        !callerAgent.useDirectCalling
    );

    const { filteredCallerAgents, filteredCalleeAgents } = useVoipAgentFiltering({
        agents,
        agentsById,
        selectedCallerId,
        callerFilterText,
        calleeFilterText,
    });

    const { handleCallerSelect, handleCalleeSelect } = useVoipPartySelection({
        agentsById,
        selectedCallerId,
        selectedCalleeId,
        setSelectedCallerId,
        setSelectedCalleeId,
        setValue,
        getValues,
    });

    return {
        filterClasses,
        isLoading,
        callerAgent,
        calleeAgent,
        showCalleeInstructions,
        callerFilterText,
        setCallerFilterText,
        calleeFilterText,
        setCalleeFilterText,
        filteredCallerAgents,
        filteredCalleeAgents,
        selectedCallerId,
        selectedCalleeId,
        handleCallerSelect,
        handleCalleeSelect,
    };
}
